// ── Window (wall-mounted) drop/save commit ──
// Extracted verbatim from the drag session's onPointerUp: the body of the
// `if (isWindow) { // ── Window drop logic ── }` branch. The non-window branch has lived
// in ./dropCommit.ts since an earlier step; this is its sibling.
//
// Variables that used to come from the enclosing closures arrive through one explicit
// context object. The drop is saved local-first via ../state/layoutSync.ts — on a red
// spot too, like any furniture (it used to be skipped, which left the item showing the
// OTHER wall's facing after a move across the corner). Returns whether the spot is valid.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import useFurniturePreviewStore from '../../../stores/useFurniturePreviewStore';
import { stageLayout } from '../state/layoutSync';
import { validateWindowPlacement } from '../../../domain/windowPlacement';
import type { RenderFurniture } from '../../../domain/types';
import { asFurnitureSprite } from '../render/pixiExtensions';
import {
  snapWindowAnchorToGrid as snapWindowAnchorToGridImported,
  WINDOW_VERTICAL_OFFSET,
  wallItemHeightPx,
  wallVerticalSpan,
} from '../../../domain/windowSnap';

export interface WindowDropCommitContext {
  /** The dropped item's PIXI objects plus the spring-back position captured at drag start. */
  target: {
    currentMarker: PIXI.Container;
    currentSprite: PIXI.Sprite | undefined;
    origX: number;
    origY: number;
  };
  /** Final marker position at drop time (read once in onPointerUp before this call). */
  drop: { newX: number; newY: number };
  furniture: RenderFurniture;
  geometry: {
    tileDimW: number | null | undefined;
    tileDimH: number | null | undefined;
    interactionIsRug: boolean;
    rawEffectiveWidthTiles: number;
    rawEffectiveHeightTiles: number;
    liveWallFloorOffset: number;
  };
  interiorMarkers: Map<number, PIXI.Container>;
  services: { mapObjectId: number; queryClient: QueryClient; t: (key: string) => string };
  previewActions: {
    updatePreviewPosition: (instanceId: number, x: number, y: number) => void;
    togglePreviewMirror: (instanceId: number) => void;
  };
  logFurnitureDiagnostics: () => void;
  /** Height on the wall the drag chose (wall_floor_offset); falls back to the live one. */
  dropWallOffset?: number;
}

export async function commitWindowDrop(ctx: WindowDropCommitContext): Promise<boolean> {
  const {
    target: { currentSprite },
    drop: { newX, newY },
    furniture,
    geometry: {
      rawEffectiveWidthTiles,
      rawEffectiveHeightTiles,
      liveWallFloorOffset,
    },
    interiorMarkers,
    services: { mapObjectId, queryClient },
    previewActions: { updatePreviewPosition, togglePreviewMirror },
    logFurnitureDiagnostics,
    dropWallOffset,
  } = ctx;

  // Wherever it was released it stays — on a red spot too (like any furniture); the
  // tint only tells the player. Its facing is the wall's (set during the drag), and
  // its height on the wall is the one the drag chose.
  const isMirrored = currentSprite ? currentSprite.scale.x < 0 : false;
  const wallOffset = dropWallOffset ?? liveWallFloorOffset;
  const floorY = newY - WINDOW_VERTICAL_OFFSET - wallOffset;
  const dropSnap = snapWindowAnchorToGridImported(
    newX, floorY,
    rawEffectiveWidthTiles,
    rawEffectiveHeightTiles,
  );
  const dropOverlayW = isMirrored ? rawEffectiveHeightTiles : rawEffectiveWidthTiles;
  const dropOverlayH = isMirrored ? rawEffectiveWidthTiles : rawEffectiveHeightTiles;
  const itemHeight = currentSprite ? wallItemHeightPx(currentSprite.width, currentSprite.height) : 0;
  const { valid } = validateWindowPlacement(
    interiorMarkers,
    dropSnap.baseTileX, dropSnap.baseTileY,
    dropOverlayW, dropOverlayH,
    furniture.id,
    wallVerticalSpan(wallOffset, rawEffectiveHeightTiles, itemHeight),
  );
  if (currentSprite) {
    currentSprite.tint = valid ? 0xFFFFFF : 0xFF4444;
    currentSprite.alpha = valid ? 1 : 0.5;
  }

  // Keep the render data in step so a re-render does not move it back.
  if (currentSprite) {
    const fs = asFurnitureSprite(currentSprite)!;
    fs.furnitureData = { ...(fs.furnitureData ?? furniture), is_mirrored: isMirrored, wall_floor_offset: wallOffset };
  }

  // Save position WITHOUT vertical offsets (backend stores floor position)
  if (furniture.is_preview) {
    // Sync preview store mirror state if it changed during drag.
    // Must compare against the STORE's current value — not furniture.is_mirrored
    // (stale closure) and not sprite.furnitureData.is_mirrored (mutated during drag).
    const previewInstanceId = -(furniture.id + 100000);
    const storeIsMirrored = useFurniturePreviewStore.getState().previewItems
      .find(p => p.instanceId === previewInstanceId)?.isMirrored ?? false;
    if (isMirrored !== storeIsMirrored) {
      togglePreviewMirror(previewInstanceId);
    }
    updatePreviewPosition(previewInstanceId, newX, floorY);
  } else {
    stageLayout(queryClient, mapObjectId, [{
      id: furniture.id,
      x: newX,
      y: floorY,
      is_mirrored: isMirrored,
      wall_floor_offset: Math.round(wallOffset),
    }]);
    logFurnitureDiagnostics();
  }
  return valid;
}
